import ERP from "@apiV2/ERP.mjs";
import Schema from "@apiV2/Schema.mjs";
import View from "@apiV2/View.mjs";
import EntityService from "@/services/entityService";
import {
  toIsoDay, hojeIso, textoDe, idDe, unicos, resumirContasAPagar, maiorAtraso
} from "@/views/Relatorios/services/relatorioGeralUtils";

// Relatório Geral de Pedido de Compra (Financeiro).
// Fontes (todas já existentes no ERP, sem view nova):
//  - view "pedido-de-compra-visualizar-pedidos-de-compra": pedido, fornecedor, status, recebimento,
//    dataCriacao, itens (com jobCodigo) -- mesma tela "Visualizar Pedidos de Compra".
//  - view "contas-a-pagar" (grupos tipo "Pedido de Compra"): valores/parcelas/pagamentos --
//    mesmos valores exibidos na tela de Contas a Pagar, somados por pedido.
//  - schema RevisaoDePedidoDeCompra: data da última revisão (best-effort).

const lista = (resposta) => (resposta?.status === 200 && Array.isArray(resposta?.data) ? resposta.data : []);

const carregarUltimasRevisoes = async () => {
  const mapa = {};
  try {
    const service = new EntityService(Schema.REVISAO_PEDIDO_DE_COMPRA);
    const resposta = await service.getAll(1, 5000, "", "", null, null, null, "");
    const registros = resposta?.data?.data?.data || (Array.isArray(resposta?.data) ? resposta.data : []);
    (Array.isArray(registros) ? registros : []).forEach((rev) => {
      const pedidoId = idDe(rev.pedidoDeCompra);
      const dia = toIsoDay(rev.dataRevisao || rev.createAt || rev.dataCriacaoSistema || rev.dataCriacao);
      if (pedidoId && dia && (!mapa[pedidoId] || dia > mapa[pedidoId])) mapa[pedidoId] = dia;
    });
  } catch (e) {
    // best-effort: sem revisões a coluna fica vazia
  }
  return mapa;
};

const jobsDoPedido = (dado) =>
  unicos((dado.itens || []).map((i) => i?.jobCodigo || textoDe(i?.job))).sort();

const fornecedorTexto = (dado) => {
  const f = dado.fornecedor || {};
  const nome = textoDe(f.nome || f.nomeFantasia || f.razaoSocial || f);
  const codigo = f.codigo ? String(f.codigo).trim() : "";
  return codigo && nome ? `${codigo} - ${nome}` : nome || codigo;
};

export default {
  async listar() {
    const [respPedidos, respContas, revisoes] = await Promise.all([
      ERP.getManyWithView(View.PEDIDO_DE_COMPRA_VISUALIZAR_PEDIDOS_DE_COMPRA),
      ERP.getManyWithView(View.CONTAS_A_PAGAR),
      carregarUltimasRevisoes()
    ]);

    const pedidos = lista(respPedidos);
    const gruposPorId = {};
    const gruposPorCodigo = {};
    lista(respContas)
      .filter((g) => g?.tipo === "Pedido de Compra")
      .forEach((g) => {
        if (g.id) gruposPorId[g.id] = g;
        if (g.codigo) gruposPorCodigo[String(g.codigo)] = g;
      });

    const hoje = hojeIso();

    return pedidos.map((dado) => {
      const grupo = gruposPorId[dado.id] || gruposPorCodigo[String(dado.codigo)] || null;
      const resumo = resumirContasAPagar(grupo);
      const vencimentos = unicos(resumo.parcelas.map((p) => p.vencimento));
      const atraso = /cancel/i.test(dado.status || "") ? 0 : maiorAtraso(resumo.parcelas, hoje);

      return {
        _id: dado.id || dado.codigo,
        pedido: dado.codigo || "",
        fornecedor: fornecedorTexto(dado),
        job: jobsDoPedido(dado).join(", "),
        dataEmissao: toIsoDay(dado.dataCriacao),
        vencimentos,
        dataUltimoPagamento: resumo.ultimoPagamento,
        dataUltimaRevisao: revisoes[dado.id] || "",
        status: dado.status || "",
        recebimento: dado.recebimento || "",
        ...resumo.soma,
        semContaAPagar: !resumo.existe,
        vencido: atraso > 0,
        diasAtraso: atraso,
        parcelas: resumo.parcelas,
        itens: (dado.itens || []).map((i) => ({
          item: i?.sequencia ?? i?.item ?? "",
          codigo: i?.itemCodigo || i?.codigo || "",
          descricao: i?.descricao || i?.itemDescricao || "",
          job: i?.jobCodigo || "",
          os: i?.osCodigo || "",
          quantidade: i?.quantidade ?? "",
          valorUnitario: i?.valorUnitario ?? "",
          valorTotal: i?.valorTotal ?? i?.valor ?? ""
        }))
      };
    });
  }
};
